import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { makeStyles, Tooltip } from '@material-ui/core';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Colors, ScriptPathTypesV2, ScriptStepV2 } from 'typings';
import { AppDispatch, RootState } from 'state/store';
import {
  apiClearScriptAnswerV2,
  apiUpdateScriptAnswerV2,
  clearScrollToCurrentStep,
} from 'state/script.state';
import ScriptStepAnswer from './ScriptStepAnswer';
import ScriptStepChoices from './ScriptStepChoices';
import ScriptStepClear from './ScriptStepClear';
import ScriptStepCopy from './ScriptStepCopy';
import ScriptStepFollowUps from './ScriptStepFollowUps';
import ScriptStepInternalNote from './ScriptStepInternalNote';
import ScriptStepSkeletonLoader from './ScriptStepSkeletonLoader';
import ScriptStepToggle from './ScriptStepToggle';
import LayersRoundedIcon from '@material-ui/icons/LayersRounded';

interface ScriptStepProps {
  isCurrentStep: boolean;
  scriptStep: ScriptStepV2;
}

interface PreviousState {
  isCollapsed?: boolean;
  otherValue?: string;
  selectedPathValue?: string;
}

const useStyles = makeStyles((theme) => ({
  container: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    position: 'relative',
  },
  containerAnswered: {
    background:
      theme.palette.type === 'light'
        ? theme.palette.background.custom
        : theme.palette.action.selected,
    boxShadow:
      theme.palette.type === 'light'
        ? `inset 0 0 0 1px rgba(0, 0, 0, 0.1)`
        : `inset 0 0 0 1px rgba(255, 255, 255, 0.1)`,
    borderRadius: '8px',
  },
  containerCollapsed: {
    textAlign: 'center',
  },
  containerGlowing: {
    animation: '$glow 0.6s ease-out',
  },
  containerUpdating: {
    // every step after a step that's updating should disappear
    '& ~ .script-step': {
      display: 'none',
    },
  },

  messageText: {
    marginBottom: theme.spacing(2),
    whiteSpace: 'pre-wrap',
  },
  messageTemplateIcon: {
    color: Colors.MEDIUM_GRAY,
    display: 'inline-block',
    fontSize: '1.2rem',
    marginBottom: '-5px',
    paddingLeft: '5px',
  },

  actions: {
    display: 'flex',
    justifyContent: 'space-between',
  },

  '@keyframes glow': {
    '0%': {
      boxShadow:
        theme.palette.type === 'light'
          ? `inset 0 0 0 2px rgba(0, 0, 0, 0.3)`
          : `inset 0 0 0 2px rgba(255, 255, 255, 0.6)`,
    },
    '75%': {
      boxShadow:
        theme.palette.type === 'light'
          ? `inset 0 0 0 2px rgba(0, 0, 0, 0.3)`
          : `inset 0 0 0 2px rgba(255, 255, 255, 0.6)`,
    },
    '100%': {
      boxShadow:
        theme.palette.type === 'light'
          ? `inset 0 0 0 1px rgba(0, 0, 0, 0.1)`
          : `inset 0 0 0 1px rgba(255, 255, 255, 0.1)`,
    },
  },
}));

const ScriptStep = ({ isCurrentStep, scriptStep }: ScriptStepProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const styles = useStyles();

  const { isUpdating, shouldScroll } = useSelector(
    (state: RootState) => ({
      isUpdating: state.script.updatingStepId === scriptStep.id,
      shouldScroll: state.script.shouldScrollToCurrentStep,
    }),
    shallowEqual
  );

  // auto-scroll to the current step
  const targetRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!shouldScroll || !isCurrentStep) return;

    if (targetRef.current) {
      targetRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }

    dispatch(clearScrollToCurrentStep());
  }, [dispatch, shouldScroll, isCurrentStep]);

  // when no "other" value is specified, we need to save a default value
  const defaultOtherValue = '-';

  // track whether the step is collapsed (only shows the current answer) or expanded (shows the choices)
  const [isCollapsed, setIsCollapsed] = useState(!!scriptStep.answer);
  const [isGlowing, setIsGlowing] = useState(false);

  // track the value of the selected path (could be the "other", in which case we need more information)
  const [selectedPathValue, setSelectedPathValue] = useState(() => {
    if (!scriptStep.answer) {
      return '';
    }

    return (
      scriptStep.scriptPaths.find((scriptPath) => scriptPath.value === scriptStep.answer)?.value ??
      scriptStep.scriptPaths.find((scriptPath) => scriptPath.type === ScriptPathTypesV2.OTHER)
        ?.value ??
      scriptStep.answer
    );
  });

  // track the custom value if "other" is selected
  const [otherValue, setOtherValue] = useState(() => {
    if (!scriptStep.answer) {
      return '';
    }

    // if a non-"other" path is selected, we don't have an "other" value
    const selectedChoice = scriptStep.scriptPaths.find(
      (scriptPath) => scriptPath.value === scriptStep.answer
    );
    if (selectedChoice && selectedChoice.type !== ScriptPathTypesV2.OTHER) {
      return '';
    }

    // if we have an "other" path, return the answer as long as it's not the default value
    const hasOther = !!scriptStep.scriptPaths.find(
      (scriptPath) => scriptPath.type === ScriptPathTypesV2.OTHER
    );
    return hasOther && scriptStep.answer !== defaultOtherValue ? scriptStep.answer : '';
  });

  // send the API call to clear the answer and revert to the previous state on error
  const persistClearAnswer = async (previous: PreviousState) => {
    try {
      await dispatch(
        apiClearScriptAnswerV2({
          scriptStepId: scriptStep.id,
        })
      ).unwrap();
    } catch (error) {
      revertToPrevious(previous);
    }
  };

  // send the API call to update the answer and revert to the previous state on error
  const persistUpdateAnswer = async (answer: string, previous: PreviousState) => {
    try {
      await dispatch(
        apiUpdateScriptAnswerV2({
          scriptStepId: scriptStep.id,
          answer,
        })
      ).unwrap();
    } catch (error) {
      revertToPrevious(previous);
    }
  };

  const revertToPrevious = (previous: PreviousState) => {
    if (previous.selectedPathValue !== undefined) {
      setSelectedPathValue(previous.selectedPathValue);
    }

    if (previous.otherValue !== undefined) {
      setOtherValue(previous.otherValue);
    }

    if (previous.isCollapsed !== undefined) {
      setIsCollapsed(previous.isCollapsed);
    }
  };

  const startGlow = () => {
    setIsGlowing(true);

    setTimeout(() => {
      setIsGlowing(false);
    }, 1000);
  };

  const onPathClear = async () => {
    const previous: PreviousState = {
      isCollapsed,
      otherValue,
      selectedPathValue,
    };

    setSelectedPathValue('');
    setOtherValue('');
    setIsCollapsed(false);

    await persistClearAnswer(previous);
  };

  const onPathChange = async (value: string) => {
    // if we have no value, we can't do anything
    if (!value) return;

    const previous: PreviousState = {
      isCollapsed,
      otherValue,
      selectedPathValue,
    };

    setSelectedPathValue(value);
    const choice = scriptStep.scriptPaths.find((scriptPath) => scriptPath.value === value) ?? null;

    // we chose the "other" path, let's collapse and persist (using the default value if none is set)
    if (choice?.type === ScriptPathTypesV2.OTHER) {
      setIsCollapsed(true);
      startGlow();

      await persistUpdateAnswer(otherValue || defaultOtherValue, previous);
      return;
    }

    // for any other path, let's clear the "other" value, collapse, and persist
    setOtherValue('');
    setIsCollapsed(true);
    startGlow();

    await persistUpdateAnswer(value, previous);
  };

  const onOtherChange = async (value: string) => {
    // we have set a new "other" value, we can now collapse and persist (using the default value if none is set)
    if (value !== otherValue) {
      const previous: PreviousState = {
        isCollapsed: false, // always re-open the step
        otherValue,
      };

      setOtherValue(value);
      setIsCollapsed(true);
      startGlow();

      await persistUpdateAnswer(value || defaultOtherValue, previous);
    }
  };

  const isAnswered = !!selectedPathValue || !!otherValue;
  const selectedPath =
    scriptStep.scriptPaths.find((scriptPath) => scriptPath.value === selectedPathValue) ?? null;

  return (
    <>
      <div
        className={cx('script-step', styles.container, {
          [styles.containerAnswered]: isAnswered,
          [styles.containerCollapsed]: isCollapsed,
          [styles.containerGlowing]: isAnswered && isGlowing,
          [styles.containerUpdating]: isUpdating,
        })}
        ref={targetRef}
      >
        {!!scriptStep.internalNoteSegments && (
          <ScriptStepInternalNote segments={scriptStep.internalNoteSegments} />
        )}
        {!!scriptStep.messageText && (
          <p className={styles.messageText}>
            {scriptStep.messageText}
            {scriptStep.messageTextUsesTemplate && (
              <Tooltip title="Alternate version may be shown" placement="top" arrow>
                <LayersRoundedIcon className={styles.messageTemplateIcon} />
              </Tooltip>
            )}
          </p>
        )}
        {!isCollapsed && !!scriptStep.scriptFollowUps && (
          <ScriptStepFollowUps
            isCurrentStep={isCurrentStep}
            scriptFollowUps={scriptStep.scriptFollowUps}
          />
        )}
        {isCollapsed && !!selectedPath && (
          <>
            <ScriptStepAnswer
              initialOtherValue={otherValue}
              onOtherChange={(value: string) => onOtherChange(value)}
              scriptPath={selectedPath}
            />
            {!!scriptStep.updateAction && <ScriptStepClear onClear={() => onPathClear()} />}
          </>
        )}
        {!isCollapsed && (
          <>
            <ScriptStepChoices
              initialOtherValue={otherValue}
              onPathChange={(value: string) => onPathChange(value)}
              onOtherChange={(value: string) => onOtherChange(value)}
              scriptPaths={scriptStep.scriptPaths}
              selectedPath={selectedPath}
            />
            <div className={styles.actions}>
              <div>
                {!!scriptStep.updateAction && isAnswered && (
                  <ScriptStepClear onClear={() => onPathClear()} />
                )}
              </div>
              <div>
                {!!scriptStep.messageText && (
                  <ScriptStepCopy
                    isCurrentStep={isCurrentStep}
                    messageText={scriptStep.messageText}
                  />
                )}
              </div>
            </div>
          </>
        )}
        {isAnswered && (
          <ScriptStepToggle
            isCollapsed={isCollapsed}
            onClick={() => setIsCollapsed(!isCollapsed)}
            pathHasNextStep={selectedPath?.hasNextStep ?? true}
            pathType={selectedPath?.type ?? null}
          />
        )}
      </div>
      {isUpdating && <ScriptStepSkeletonLoader />}
    </>
  );
};

export default ScriptStep;
